import React, { useContext, useReducer, useRef, useEffect, useCallback, useState } from 'react';
import { modifiersReducer } from './modifiersReducer';
import {
	IFilterValues,
	ModifiersContext,
	modifiersInitialState,
	TModifiersCountOfItems,
	TModifiersSortBy,
	TTypeOfModalWindow,
} from './modifiersContext';
import { AppContext, IAppContext } from 'appState/appContext';
import { ModifiersActionTypes } from './modifiersActions';
import { useFetchData } from 'hooks/useFetchData';
import { getPaginatedModifiers, deleteModifier } from 'backend/modifiers';
import { IModifiers, IGetPaginatedModifiers } from 'types';
import { AxiosResponse } from 'axios';

const ModifiersState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(modifiersReducer, modifiersInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [allowChangingSearchString, setAllowChangingSearchString] = useState(false);
	const isMounted = useRef<boolean>(false);
	const { refetch, response, loading } = useFetchData<AxiosResponse<IModifiers>, IGetPaginatedModifiers>(
		isMounted.current ? getPaginatedModifiers : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchByName: state.searchString ? state.searchString : null,
			customerId: state.IDSelectedCustomer,
			restaurantId: state.IDSelectedRestaurant,
			modifierGroupId: state.IDSelectedModifierGroup,
			priceFrom: state.priceFrom ? Number(state.priceFrom) : null,
			priceTo: state.priceTo ? Number(state.priceTo) : null,
			isDisabled: state.isDisabledModifier,
			isRestricted: state.isRestrictedModifier,
			isShowOnlyComboItems: state.isShowOnlyComboItems,
			sortByName: state.sortBy === 'NameA-Z' || state.sortBy === 'NameZ-A' ? state.sortBy : null,
			sortByPrice: state.sortBy === 'Price1-10' || state.sortBy === 'Price10-1' ? state.sortBy : null,
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: ModifiersActionTypes.SET_MODIFIERS,
				payload: {
					modifiers: response.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
			setAllowChangingSearchString(true);
		}
	}, [response]);

	const deleteSelectedItem = useCallback(async () => {
		try {
			showLoader();
			await deleteModifier(state.IDForDeletion);
			const filteredModifiers = state.modifiers.modifiers.filter((el) => el.id !== state.IDForDeletion);
			dispatch({
				type: ModifiersActionTypes.SET_MODIFIERS,
				payload: {
					modifiers: {
						modifiersCount: state.modifiers.modifiersCount - 1,
						modifiers: filteredModifiers,
					},
					typeOfModalWindow: 'success',
				},
			});
		} catch (err) {
			dispatch({
				type: ModifiersActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
		} finally {
			hideLoader();
		}
	}, [showLoader, hideLoader, state.IDForDeletion, state.modifiers]);

	const changeSearchString = useCallback(
		(value: string) => {
			if (allowChangingSearchString) {
				dispatch({
					type: ModifiersActionTypes.CHANGE_SEARCH_STRING,
					payload: {
						searchString: value,
					},
				});
			}
		},
		[allowChangingSearchString],
	);

	const setIDCustomer = useCallback((value: Nullable<string>) => {
		dispatch({
			type: ModifiersActionTypes.SET_ID_SELECTED_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	}, []);

	const setIDRestaurant = useCallback((value: Nullable<string>) => {
		dispatch({
			type: ModifiersActionTypes.SET_ID_SELECTED_RESTAURANT,
			payload: {
				IDSelectedRestaurant: value,
			},
		});
	}, []);

	const setIDModifierGroup = useCallback((value: Nullable<string>) => {
		dispatch({
			type: ModifiersActionTypes.SET_ID_SELECTED_MODIFIER_GROUP,
			payload: {
				IDSelectedModifierGroup: value,
			},
		});
	}, []);

	const changePriceFrom = useCallback((value: string) => {
		dispatch({
			type: ModifiersActionTypes.CHANGE_PRICE_FROM,
			payload: {
				priceFrom: value,
			},
		});
	}, []);

	const changePriceTo = useCallback((value: string) => {
		dispatch({
			type: ModifiersActionTypes.CHANGE_PRICE_TO,
			payload: {
				priceTo: value,
			},
		});
	}, []);

	const toggleShowOnlyComboItems = useCallback(() => {
		dispatch({
			type: ModifiersActionTypes.TOGGLE_SHOW_ONLY_COMBO_ITEMS,
		});
	}, []);

	const resetSelectedControls = useCallback(() => {
		dispatch({
			type: ModifiersActionTypes.RESET_SELECTED_CONTROLS,
		});
	}, []);

	const setSortBy = useCallback((value: TModifiersSortBy) => {
		dispatch({
			type: ModifiersActionTypes.SET_SORT_BY,
			payload: {
				sortBy: value,
			},
		});
	}, []);

	const setCountOfItems = useCallback((value: TModifiersCountOfItems) => {
		dispatch({
			type: ModifiersActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: value,
			},
		});
	}, []);

	const toggleIsDisabledModifiers = useCallback(() => {
		dispatch({
			type: ModifiersActionTypes.TOGGLE_IS_DISABLED_MODIFIERS,
		});
	}, []);

	const toggleIsRestrictedModifiers = useCallback(() => {
		dispatch({
			type: ModifiersActionTypes.TOGGLE_IS_RESTRICTED_MODIFIERS,
		});
	}, []);

	const changeCurrentPage = useCallback((value: number) => {
		dispatch({
			type: ModifiersActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: value,
			},
		});
	}, []);

	const setIDForDeletion = useCallback((value: string) => {
		dispatch({
			type: ModifiersActionTypes.SET_ID_FOR_DELETION,
			payload: {
				IDForDeletion: value,
				typeOfModalWindow: 'toConfirmDeleteItem',
			},
		});
	}, []);

	const setIDForFullInfo = useCallback((value: string) => {
		dispatch({
			type: ModifiersActionTypes.SET_ID_FOR_FULL_INFO,
			payload: {
				IDForFullInfo: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: ModifiersActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const setFiltersFromQueryString = useCallback(
		(value: IFilterValues) => {
			dispatch({
				type: ModifiersActionTypes.SET_FILTERS_FROM_QUERY_STRING,
				payload: {
					filters: value,
				},
			});
			isMounted.current = true;
			refetch();
		},
		[refetch, isMounted],
	);

	return (
		<ModifiersContext.Provider
			value={{
				deleteSelectedItem,
				changeSearchString,
				setIDCustomer,
				setIDRestaurant,
				setIDModifierGroup,
				changePriceFrom,
				changePriceTo,
				toggleShowOnlyComboItems,
				resetSelectedControls,
				setSortBy,
				setCountOfItems,
				toggleIsDisabledModifiers,
				toggleIsRestrictedModifiers,
				changeCurrentPage,
				setIDForDeletion,
				setIDForFullInfo,
				changeTypeOfModalWindow,
				setFiltersFromQueryString,
				modifiers: state.modifiers,
				searchString: state.searchString,
				IDSelectedCustomer: state.IDSelectedCustomer,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				IDSelectedModifierGroup: state.IDSelectedModifierGroup,
				priceFrom: state.priceFrom,
				priceTo: state.priceTo,
				isShowOnlyComboItems: state.isShowOnlyComboItems,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				isDisabledModifier: state.isDisabledModifier,
				isRestrictedModifier: state.isRestrictedModifier,
				currentPage: state.currentPage,
				IDForDeletion: state.IDForDeletion,
				IDForFullInfo: state.IDForFullInfo,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</ModifiersContext.Provider>
	);
};

export default ModifiersState;
